import { Page, Site, SiteVisibilityReport } from "~/api/Api";
import { PaginationConfig } from "~/components/ui/pagination/Pagination";
import VisibilityImprovementBanner from "~/components/visibilityMetrics/VisibilityImprovementBanner";
import VisibilityMetricsTable from "~/components/visibilityMetrics/VisibilityMetricsTable";

import styles from "../styles.module.scss";

/**
 * DEV-ONLY visual sandbox for the Visibility Metrics before/after work.
 *
 * Local environments can't reproduce a healthy AI Visibility Engine with real
 * crawl data, so this page feeds a spread of hardcoded scenarios into the REAL
 * table/banner components. Because it uses the real components and their SCSS,
 * what you see here matches the production page. All fake data is confined to
 * this file and the route is gated behind import.meta.env.DEV, so nothing leaks
 * into production.
 */

const makePage = (
  path: string,
  textRatio: number | null,
  contentOverlap: number,
  loadMs: number,
): Page =>
  ({
    id: path,
    url: `https://example.com${path}`,
    path_and_query: path,
    visibility_report:
      textRatio === null
        ? null
        : {
            platform: "desktop",
            segment: "partially_accessible",
            text_ratio: textRatio,
            content_overlap: contentOverlap,
          },
    performance_reports: [
      { platform: "desktop", page_load: loadMs, absolute_ttfb: 220 },
    ],
  }) as unknown as Page;

// (path, text_ratio, content_overlap, load_ms) chosen to exercise every tier,
// the slow-load penalty, the "already perfect" skip, and the empty state.
const SCENARIO_PAGES: Page[] = [
  makePage("/invisible-page", 0.08, 0.15, 1200),
  makePage("/barely-visible", 0.3, 0.34, 1500),
  makePage("/half-visible-fast", 0.55, 0.5, 900),
  makePage("/half-visible-slow-load", 0.78, 0.7, 5000),
  makePage("/partially-visible", 0.7, 0.66, 1000),
  makePage("/mostly-visible", 0.85, 0.82, 800),
  makePage("/already-fully-visible", 0.96, 0.99, 700),
  makePage("/no-report-yet", null, 0, 0),
];

const PAGINATION: PaginationConfig = {
  currentPage: 1,
  itemsPerPage: 10,
  nextPage: null,
  prevPage: null,
  totalPages: 1,
  totalItems: SCENARIO_PAGES.length,
};

const HEALTHY_SITE = {
  prerendering: true,
  delegated_domain: { status: { setup: true, verification: true } },
} as unknown as Site;

const BASELINE_SITE = { prerendering: false } as unknown as Site;

const BANNER_REPORT = {
  platform: "desktop",
  pages_analyzed: 128,
  avg_text_ratio: 0.41,
  avg_content_overlap: 0.47,
} as unknown as SiteVisibilityReport;

const noop = () => {};

const VisibilityEngineMetricsPreview = () => (
  <div className={styles.page}>
    <h1 className={styles.title}>Visibility Metrics — Preview</h1>

    <h2 className={styles.title} style={{ fontSize: "1.1rem" }}>
      Engine healthy (before → after)
    </h2>
    <VisibilityImprovementBanner report={BANNER_REPORT} />
    <VisibilityMetricsTable
      site={HEALTHY_SITE}
      data={{ pages: SCENARIO_PAGES } as never}
      pagination={PAGINATION}
      setPagination={noop}
      isLoading={false}
    />

    <h2
      className={styles.title}
      style={{ fontSize: "1.1rem", marginTop: "var(--xl)" }}
    >
      Engine not active (baseline only)
    </h2>
    <VisibilityImprovementBanner report={BANNER_REPORT} variant="projection" />
    <VisibilityMetricsTable
      site={BASELINE_SITE}
      data={{ pages: SCENARIO_PAGES } as never}
      pagination={PAGINATION}
      setPagination={noop}
      isLoading={false}
    />
  </div>
);

export default VisibilityEngineMetricsPreview;
